import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { Feature, Point } from 'geojson';
import {
    FullscreenControl,
    GeoJSONSource,
    GeoJSONSourceSpecification,
    GeolocateControl,
    LngLatLike,
    Map,
    MapMouseEvent,
    Marker,
    NavigationControl,
    Popup,
    ScaleControl
} from 'maplibre-gl';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Icon } from 'src/app/types/icon';
import { MAPTILER_KEY } from '../../shared/app-token';
import { ResetControl } from './reset-control';

/** type of feature marker */
type FeatureMarker = Feature<Point>;

/**
 * map component
 * generates an interactive map
 * based on maptiler api
 * @class MapComponent
 * @typedef {MapComponent}
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnDestroy}
 */
@Component( {
    selector: 'riege-map',
    imports: [CommonModule],
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    standalone: true
} )
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
    /** flag, show controls on map */
    @Input() showControls = true;
    /** flag, allow fullscreen map */
    @Input() allowFullscreen = false;
    /** flag, allow user localisation */
    @Input() allowLocateUser = false;
    /** flag, show scale on map */
    @Input() showScale = false;
    /** flag, show reset control */
    @Input() showReset = false;
    /** flag, show zoom controls */
    @Input() showZoom = true;

    /** inputs to set basic map coordinates */
    @Input() center?: [number, number];
    /** input to overwrite default zoom */
    @Input() zoom = 12;
    /** broadcast event on map movement */
    @Output() mapMoved: EventEmitter<any> = new EventEmitter();

    /** reference on map */
    map!: Map;

    /** reference on map container */
    @ViewChild( 'map' )
    private mapContainer!: ElementRef<HTMLElement>;

    /** reference on clicked point on map */
    #selectedPoint?: any;

    /** reference on data source id */
    sourceId?: string;

    /** reference on geoJSON holding the cluster data */
    #geoJson?: GeoJSONSourceSpecification;

    /**  lifecycle hook */
    readonly loaded: Subject<boolean> = new Subject<boolean>();

    /** maptiler api settings */
    #maptiler_key = inject( MAPTILER_KEY );
    #maptiler_api = 'https://api.maptiler.com/maps/560b1ec1-afd7-4616-8a4d-14a51488b332';
    #maptiler_url = `${this.#maptiler_api}/style.json?key=${this.#maptiler_key}`;
    #mapLanguage = 'en';

    /** map center defaults to Riege HQ Meerbusch */
    #initialState = {
        center: [6.608872775079373, 51.28643943986161] as [number, number],
        zoom: 10,
        language: 'en'
    };

    /** list of localized strings for map controls */
    readonly locale = {
        'GeolocateControl.FindMyLocation': $localize`:@@map-geolocatecontrol-findmylocation:Show my location`,
        'GeolocateControl.LocationNotAvailable': $localize`:@@map-geolocatecontrol-locationnotavailable:Location not available`,
        'NavigationControl.ResetBearing': $localize`:@@map-navigationcontrol-resetbearing:Reset bearing to north`,
        'NavigationControl.ZoomIn': $localize`:@@map-navigationcontrol-zoomin:Zoom in`,
        'NavigationControl.ZoomOut': $localize`:@@map-navigationcontrol-zoomout:Zoom out`
    };

    /** inject cookieservice - use to retrieve user language for translation */
    #cookieService = inject( CookieService );

    /** on init set map to user language, defaults to english */
    ngOnInit(): void {
        this.#mapLanguage = this.#cookieService.get( 'language' ) || this.#initialState.language;
    }
    /** initialize map when view is done */
    ngAfterViewInit() {
        this.map = new Map( {
            attributionControl: false,
            container: this.mapContainer.nativeElement,
            center: this.center ? this.center : this.#initialState.center,
            maxZoom: 16,
            minZoom: 6,
            locale: this.locale,
            style: `${this.#maptiler_url}`,
            zoom: this.zoom ? this.zoom : this.#initialState.zoom
        } ).on( 'load', () => this.setMap() );
    }

    /** set map controls according to inputs */
    setMap() {
        this.loaded.next( true );
        this.changeLangTo( this.#mapLanguage );

        // set controls if configured
        if ( this.showControls ) {
            if ( this.allowFullscreen ) this.map.addControl( new FullscreenControl( {} ), 'top-right' );

            if ( this.showZoom ) this.map.addControl( new NavigationControl( {} ), 'top-right' );

            if ( this.showReset ) this.map.addControl( new ResetControl(), 'top-right' );

            if ( this.allowLocateUser )
                this.map.addControl(
                    new GeolocateControl( { fitBoundsOptions: { maxZoom: this.#initialState.zoom } } ),
                    'top-right'
                );

            if ( this.showScale ) this.map.addControl( new ScaleControl( { maxWidth: 80, unit: 'metric' } ) );
        }

        this.map.on( 'moveend', ( $event ) => {
            this.mapMoved.emit( $event );
        } );
    }

    /**
     * helper function to change the language of various map layers
     * @param {string} language language code
     */
    changeLangTo( language: string ) {
        if ( !this.map ) return;

        const layers = ['country', 'place', 'airport', 'station'];
        layers.forEach( ( layer ) => {
            this.map.setLayoutProperty( layer, 'text-field', '{name:' + language + '}' );
        } );
    }
    /**
     * helper function to center map
     * to a given location of a clicked point / marker
     */
    centerMapTo( evt: MapMouseEvent ) {
        const element = ( evt as any ).features[0];
        // center map
        const pos: LngLatLike = {
            lat: element.geometry.coordinates[1],
            lng: element.geometry.coordinates[0]
        };
        // animate to position
        this.map.easeTo( { duration: 1000, animate: true, center: pos } );

        // only zoom in clusters
        if ( element.properties.cluster ) {
            const currentZoom = Math.round( this.map.getZoom() );
            this.zoom = currentZoom + 1;
            this.map.zoomTo( this.zoom );
        }
        // hide existing popups
        this.#selectedPoint = undefined;
    }

    /** get bounds of map */
    getBounds() {
        return this.map.getBounds();
    }

    /**
     * control function to reset the map to initial settings
     */
    resetMap() {
        this.#selectedPoint = undefined;
        this.map.setCenter( this.#initialState.center );
        this.map.setZoom( this.#initialState.zoom );
    }

    /**
     * helper function to filter elements in a given layer
     * @param filter {object} object of properties which define by key value pairs a filter to be set
     */
    filterMapSource( filter: any ): void {
        console.log( 'filter :', filter );
        // make deep copy of geojson data
        const geoJson = JSON.parse( JSON.stringify( this.#geoJson ) );

        if ( filter.selected ) {
            // filter all entries
            const filteredFeatures = geoJson.data.features.filter(
                ( obj: any ) => obj.properties[filter.junctionType] === true
            );

            // when entries found, then overwrite source data
            if ( filteredFeatures.length ) {
                geoJson.data.features = filteredFeatures;
            }
        }

        // apply data to source
        const source: GeoJSONSource = this.map.getSource( this.sourceId! ) as GeoJSONSource;
        source.setData( geoJson.data );
    }

    /**
     * helper function to update map
     * after a location has been choosen from geocoding search
     * zooms the map to initial factor
     * @param {Feature} location - geojson point
     */
    showLocation( location: any ) {
        this.map.setCenter( location.geometry.coordinates );
        this.map.zoomTo( this.#initialState.zoom );
    }

    /**
     * helper function to create a custom icon html element
     * @param {Icon} custom icon properties
     * @returns {*} html element
     */
    createCustomIcon( custom: Icon ): any {
        const iconPath = `assets/icons/${custom.icon}`;
        const element = document.createElement( 'div' );
        element.className = 'marker';
        element.style.backgroundImage = `url(${iconPath})`;
        element.style.width = `${custom.width}px`;
        element.style.height = `${custom.height}px`;
        return element;
    }

    /** add markers to the map */
    addMarkers( markers: FeatureMarker[] ) {
        markers?.map( ( marker ) => {
            const pos: LngLatLike = [marker.geometry.coordinates[1], marker.geometry.coordinates[0]];

            const point = new Marker( {
                element: this.createCustomIcon( { icon: 'marker-agent.svg', width: 39, height: 52 } )
            } )
                .setLngLat( pos )
                .addTo( this.map );

            if ( marker.properties!['popup'] ) {
                point.setPopup(
                    new Popup( {
                        closeOnMove: false,
                        offset: [0, -15]
                    } )
                        .on( 'open', () => {
                            this.map.easeTo( {
                                duration: 500,
                                animate: true,
                                center: pos
                            } );
                        } )
                        .setHTML( marker.properties!['description'] )
                );
            }
        } );
    }

    /** update layer source data */
    updateClusterLayer( sourceId: string, data: any ) {
        const source = this.map.getSource( sourceId ) as GeoJSONSource;
        source.setData( data );
    }

    /** add cluster layers */
    async addClusterLayer( sourceId: string, data: any ) {
        // set sourceId, which is used as a flag for further updates
        this.sourceId = sourceId;

        /** add a layer source, which holds the data to display on layer */
        const geoJsonSource: GeoJSONSourceSpecification = {
            type: 'geojson',
            data: data,
            cluster: true,
            clusterMaxZoom: 15,
            clusterRadius: 50
        };
        this.#geoJson = geoJsonSource;
        this.map.addSource( sourceId, geoJsonSource );

        // add a cluster layer, which display the amount of clustered points
        this.map.addLayer( {
            id: 'clusters',
            type: 'circle',
            source: sourceId,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': {
                    property: 'point_count',
                    type: 'interval',
                    stops: [
                        [0, '#ebc42e'],
                        [100, '#ebc42e'],
                        [750, '#ebc42e']
                    ]
                },
                'circle-radius': {
                    property: 'point_count',
                    type: 'interval',
                    stops: [
                        [0, 20],
                        [20, 30],
                        [40, 40]
                    ]
                }
            }
        } );

        // add eventHandler to clusters
        this.map.on( 'mouseenter', 'clusters', () => {
            this.map.getCanvas().style.cursor = 'pointer';
        } );

        this.map.on( 'mouseleave', 'clusters', () => {
            this.map.getCanvas().style.cursor = '';
        } );

        this.map.on( 'click', 'clusters', ( $event ) => {
            const features = this.map.queryRenderedFeatures( $event.point, {
                layers: ['clusters']
            } );

            const clusterId = features[0].properties['cluster_id'];
            const center = $event.lngLat;
            const source: GeoJSONSource = this.map.getSource( sourceId ) as GeoJSONSource;

            source
                .getClusterExpansionZoom( clusterId )
                .then( ( zoom ) => {
                    this.map.easeTo( {
                        center: center,
                        zoom: zoom
                    } );
                } )
                .catch();
        } );

        /** add custom icon for unclustered points */
        this.map
            .loadImage( 'assets/icons/marker-place.png' )
            .then( ( res ) => this.map.addImage( 'custom-icon', res.data ) )
            .catch( ( error ) => console.log( 'error', error ) );

        // add cluster count layer
        this.map.addLayer( {
            id: 'cluster-count',
            type: 'symbol',
            source: sourceId,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-size': 12
            }
        } );

        // add layer for unclustered points
        this.map.addLayer( {
            id: 'unclustered-point',
            type: 'symbol',
            source: sourceId,
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': 'custom-icon',
                'icon-size': 1.3
            }
        } );

        // add eventHandler to points
        this.map.on( 'mouseenter', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = 'pointer';
        } );

        this.map.on( 'mouseleave', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
        } );

        this.map.on( 'click', 'unclustered-point', ( $event ) => {
            this.centerMapTo( $event );
            this.#selectedPoint = ( $event as any ).features[0];

            const pos = this.#selectedPoint.geometry.coordinates;
            new Popup( { closeOnMove: false } )
                .setLngLat( pos )
                .setHTML( this.#selectedPoint.properties!['description'] )
                .addTo( this.map );
        } );
    }

    ngOnDestroy() {
        this.loaded.next( false );
        this.loaded.complete();
        this.map?.remove();
    }
}
